import { onBeforeUnmount, onMounted, ref } from 'vue'

function createUpdateLoop() {
  const updateFns = new Set<() => void>()

  let frameId: number | null = null

  const run = () => {
    frameId = null
    if (updateFns.size === 0) return
    updateFns.forEach(fn => fn())
    frameId = requestAnimationFrame(run)
  }

  const add = (fn: () => void) => {
    fn()
    updateFns.add(fn)
    if (frameId === null) requestAnimationFrame(run)
  }

  const remove = (fn: () => void) => {
    updateFns.delete(fn)
    if (updateFns.size === 0 && frameId !== null) {
      cancelAnimationFrame(frameId)
      frameId = null
    }
  }

  return {
    add,
    remove,
  }
}

const updateLoop = createUpdateLoop()

export function useAnimatedValue<T>(compute: () => T, areEqual: (a: T, b: T) => boolean = (a, b) => a === b) {
  const value = ref<T | null>(null)

  const update = () => {
    const newValue = compute()
    if (value.value === null || !areEqual?.(value.value, newValue)) {
      value.value = newValue
    }
  }

  onMounted(() => {
    update()
    updateLoop.add(update)
  })

  onBeforeUnmount(() => {
    updateLoop.remove(update)
  })

  return value
}
